import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, Crosshair, Target, Shield, Sparkles, Activity, ArrowUpRight, ArrowDownRight, Zap, Flame } from "lucide-react";
import { rrColor, winColor, convictionColor } from "@/lib/signalColors";
import { formatHoursShort, heatBadgeMeta } from "@/lib/format";
import TradeDialog from "@/components/TradeDialog";

const signalBadgeClass = (signal) => {
  if (signal === "STRONG BUY") return "bg-[#00FFA3]/20 text-[#00FFA3] border border-[#00FFA3]/30";
  if (signal === "BUY") return "bg-[#00FFA3]/10 text-[#00FFA3]";
  if (signal === "STRONG SELL") return "bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/30";
  if (signal === "SELL") return "bg-[#FF3B30]/10 text-[#FF3B30]";
  return "bg-gray-500/10 text-gray-400";
};

const sentimentColor = (sentiment) => {
  if (sentiment === "BULLISH") return "text-[#00FFA3]";
  if (sentiment === "BEARISH") return "text-[#FF3B30]";
  return "text-[#F59E0B]";
};

const filteredList = (signals, filter) => {
  if (filter === "buy") return signals.buy_signals;
  if (filter === "sell") return signals.sell_signals;
  if (filter === "strong") return signals.signals.filter((s) => s.conviction === "STRONG");
  return signals.signals;
};

function SignInPrompt({ onSignIn }) {
  return (
    <div className="text-center py-10 sm:py-16">
      <div className="glass-effect inline-block p-8 sm:p-12 rounded-2xl max-w-sm mx-auto">
        <Lock className="w-12 h-12 sm:w-16 sm:h-16 text-[#F59E0B] mx-auto mb-4 sm:mb-6" />
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-3" style={{ fontFamily: "Unbounded" }}>
          SIGN IN REQUIRED
        </h3>
        <p className="text-gray-400 mb-6 text-sm">
          Sign in to access AI Algorithm Signals with entry/exit prices and win probability
        </p>
        <Button
          data-testid="signals-signin-btn"
          onClick={onSignIn}
          className="bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90 font-semibold px-8"
        >
          Sign In to Unlock
        </Button>
      </div>
    </div>
  );
}

function PaywallPrompt({ onSubscribe }) {
  return (
    <div className="text-center py-10 sm:py-16">
      <div className="glass-effect inline-block p-8 sm:p-12 rounded-2xl border border-[#F59E0B]/30 max-w-md mx-auto">
        <Crosshair className="w-12 h-12 sm:w-16 sm:h-16 text-[#F59E0B] mx-auto mb-4 sm:mb-6" />
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-3" style={{ fontFamily: "Unbounded" }}>
          AI ALGORITHM
        </h3>
        <p className="text-gray-400 mb-2 text-sm">
          Unlock algorithmic trade signals with precise entry, target & stop-loss levels
        </p>
        <p className="text-xs text-gray-500 mb-6">Included with any subscription plan</p>
        <ul className="text-left text-gray-300 mb-6 space-y-2 max-w-sm mx-auto text-sm">
          <li className="flex items-center gap-2"><Crosshair className="w-4 h-4 text-[#F59E0B] shrink-0" />Scans all 30 assets in real-time</li>
          <li className="flex items-center gap-2"><Target className="w-4 h-4 text-[#F59E0B] shrink-0" />Entry price, target & stop-loss</li>
          <li className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#F59E0B] shrink-0" />Risk/reward ratio & win probability</li>
          <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#F59E0B] shrink-0" />AI-enhanced analysis on top picks</li>
        </ul>
        <Button
          data-testid="subscribe-for-signals-btn"
          onClick={onSubscribe}
          className="bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90 font-bold px-8 py-5 text-base shadow-[0_0_20px_rgba(245,158,11,0.3)]"
        >
          Subscribe to Unlock
        </Button>
        <p className="text-xs text-gray-500 mt-4">Starting at $4.99 for first month</p>
      </div>
    </div>
  );
}

function SignalStatsBar({ stats }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
      <div className="glass-effect p-3 rounded-lg border border-[#00FFA3]/20 text-center">
        <div className="text-lg sm:text-2xl font-bold text-[#00FFA3] mono">{stats.buy_count}</div>
        <div className="text-xs text-gray-400">Buy Signals</div>
      </div>
      <div className="glass-effect p-3 rounded-lg border border-[#FF3B30]/20 text-center">
        <div className="text-lg sm:text-2xl font-bold text-[#FF3B30] mono">{stats.sell_count}</div>
        <div className="text-xs text-gray-400">Sell Signals</div>
      </div>
      <div className="glass-effect p-3 rounded-lg border border-[#00E0FF]/20 text-center">
        <div className="text-lg sm:text-2xl font-bold text-[#00E0FF] mono">{stats.avg_win_probability}%</div>
        <div className="text-xs text-gray-400">Avg Win Prob</div>
      </div>
      <div className="glass-effect p-3 rounded-lg border border-[#F59E0B]/20 text-center">
        <div className={`text-lg sm:text-2xl font-bold mono ${sentimentColor(stats.market_sentiment)}`}>
          {stats.market_sentiment}
        </div>
        <div className="text-xs text-gray-400">Market Mood</div>
      </div>
    </div>
  );
}

const FILTER_CLASS = {
  all: "bg-white/15 text-white border-white/30",
  buy: "bg-[#00FFA3]/20 text-[#00FFA3] border-[#00FFA3]/30",
  sell: "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30",
  strong: "bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30",
};

function SignalFilters({ stats, filter, onFilterChange }) {
  const buttons = [
    { key: "all", label: "All (30)" },
    { key: "buy", label: `Buy (${stats.buy_count})` },
    { key: "sell", label: `Sell (${stats.sell_count})` },
    { key: "strong", label: "Strong Only" },
  ];
  return (
    <div className="flex gap-2 overflow-x-auto pb-2">
      {buttons.map((f) => (
        <Button
          key={f.key}
          data-testid={`filter-${f.key}-btn`}
          size="sm"
          variant={filter === f.key ? "default" : "outline"}
          onClick={() => onFilterChange(f.key)}
          className={`shrink-0 text-xs ${filter === f.key ? FILTER_CLASS[f.key] : "border-white/10 text-gray-400 hover:text-white"}`}
        >
          {f.label}
        </Button>
      ))}
    </div>
  );
}

export function SignalCard({ sig, index, isAuthenticated, onTakeTrade, onNavigate, boostTtlHours = 168 }) {
  const [rhOpen, setRhOpen] = useState(false);
  const isBuy = sig.signal.includes("BUY");
  const isSell = sig.signal.includes("SELL");
  const heatMeta = heatBadgeMeta(sig.heat_score || 0);
  const boostAge = sig.boost_age_hours ?? 0;
  const boostOpacity = sig.priority_boost
    ? Math.max(0.35, 1 - Math.min(boostAge / boostTtlHours, 1) * 0.65)
    : 1;
  const decayLabel = formatHoursShort(Math.max(0, boostTtlHours - boostAge));
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
    >
      <Card
        data-testid={`signal-card-${sig.symbol}`}
        className={`glass-effect cursor-pointer transition-all duration-300 ${
          isBuy ? "hover:border-[#00FFA3]/30" : isSell ? "hover:border-[#FF3B30]/30" : "hover:border-white/20"
        }`}
        onClick={() => onNavigate(`/asset/${sig.symbol}`)}
      >
        <CardContent className="p-3 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className={`px-2 py-1 rounded text-xs font-bold shrink-0 ${signalBadgeClass(sig.signal)}`}>
                {sig.signal}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-base sm:text-lg font-bold text-white mono">{sig.symbol}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${sig.type === "crypto" ? "bg-[#6366F1]/20 text-[#6366F1]" : "bg-[#00E0FF]/20 text-[#00E0FF]"}`}>
                    {sig.type.toUpperCase()}
                  </span>
                  {sig.ai_enhanced && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#F59E0B]/20 text-[#F59E0B]">AI+</span>
                  )}
                  {sig.priority_boost && (
                    <span
                      data-testid={`ai-boosted-${sig.symbol}`}
                      style={{ opacity: boostOpacity }}
                      className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#00FFA3]/20 text-[#00FFA3] border border-[#00FFA3]/30 transition-opacity duration-300"
                      title={`Priority-boosted — decays in ${decayLabel}`}
                    >
                      ★ · {decayLabel}
                    </span>
                  )}
                  {heatMeta && (
                    <span
                      data-testid={`ai-heat-${sig.symbol}`}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-bold border inline-flex items-center gap-1 ${heatMeta.bg} ${heatMeta.text} ${heatMeta.border}`}
                      title={`Signal heat ${sig.heat_score}/100 — ${(sig.heat_reasons || []).join(", ")}`}
                    >
                      <Flame className="w-2.5 h-2.5" />
                      {heatMeta.label}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">{sig.name}</p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-base sm:text-lg font-bold text-white mono">${sig.current_price.toLocaleString()}</div>
              <div className="text-xs text-gray-500">{sig.timeframe_label}</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-4 py-3 border-t border-b border-white/5">
            <div>
              <div className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider mb-1">Entry</div>
              <div className="text-sm sm:text-base font-bold text-[#00E0FF] mono">${sig.entry_price.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                Target <ArrowUpRight className="w-3 h-3 text-[#00FFA3]" />
              </div>
              <div className="text-sm sm:text-base font-bold text-[#00FFA3] mono">${sig.target_price.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                Stop <ArrowDownRight className="w-3 h-3 text-[#FF3B30]" />
              </div>
              <div className="text-sm sm:text-base font-bold text-[#FF3B30] mono">${sig.stop_loss.toLocaleString()}</div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2 sm:gap-4 pt-3">
            <div>
              <div className="text-[10px] text-gray-500 mb-0.5">R:R</div>
              <div className={`text-sm font-bold mono ${rrColor(sig.risk_reward)}`}>1:{sig.risk_reward}</div>
            </div>
            <div>
              <div className="text-[10px] text-gray-500 mb-0.5">Win Prob</div>
              <div className={`text-sm font-bold mono ${winColor(sig.win_probability)}`}>{sig.win_probability}%</div>
            </div>
            <div>
              <div className="text-[10px] text-gray-500 mb-0.5">Exp. Return</div>
              <div className={`text-sm font-bold mono ${sig.expected_return > 0 ? "text-[#00FFA3]" : "text-[#FF3B30]"}`}>
                {sig.expected_return > 0 ? "+" : ""}{sig.expected_return}%
              </div>
            </div>
            <div>
              <div className="text-[10px] text-gray-500 mb-0.5">Conviction</div>
              <div className={`text-sm font-bold ${convictionColor(sig.conviction)}`}>{sig.conviction}</div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-white/5">
            <p className="text-xs text-gray-400 leading-relaxed">{sig.ai_reasoning || sig.reasoning}</p>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-2">
                <Zap className="w-3 h-3 text-[#F59E0B] shrink-0" />
                <span className="text-[10px] text-[#F59E0B]">{sig.ai_catalyst || sig.key_catalyst}</span>
              </div>
              {isAuthenticated && sig.signal !== "HOLD" && (
                <div className="flex items-center gap-1.5">
                  {sig.type === "stock" && (
                    <Button
                      data-testid={`broker-trade-${sig.symbol}`}
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setRhOpen(true); }}
                      className="text-[10px] px-2 py-1 h-auto bg-white/5 text-white hover:bg-white/10 border border-white/15"
                    >
                      Trade
                    </Button>
                  )}
                  <Button
                    data-testid={`take-trade-${sig.symbol}`}
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onTakeTrade(sig); }}
                    className={`text-[10px] px-2 py-1 h-auto ${
                      isBuy
                        ? "bg-[#00FFA3]/20 text-[#00FFA3] hover:bg-[#00FFA3]/30 border border-[#00FFA3]/30"
                        : "bg-[#FF3B30]/20 text-[#FF3B30] hover:bg-[#FF3B30]/30 border border-[#FF3B30]/30"
                    }`}
                  >
                    Take Trade
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <TradeDialog sig={sig} open={rhOpen} onOpenChange={setRhOpen} />
    </motion.div>
  );
}

export default function AISignalsTab({
  signals,
  signalsLoading,
  signalsError,
  signalFilter,
  setSignalFilter,
  isAuthenticated,
  onSignIn,
  onSubscribe,
  onTakeTrade,
  onNavigate,
}) {
  if (signalsError === "signin") return <SignInPrompt onSignIn={onSignIn} />;
  if (signalsError === "payment") return <PaywallPrompt onSubscribe={onSubscribe} />;
  if (signalsLoading) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Activity className="w-8 h-8 mx-auto mb-4 animate-pulse text-[#F59E0B]" />
        Scanning 30 assets...
      </div>
    );
  }
  if (!signals) return null;

  const visibleSignals = filteredList(signals, signalFilter);

  return (
    <div className="space-y-4">
      <SignalStatsBar stats={signals.stats} />
      <SignalFilters stats={signals.stats} filter={signalFilter} onFilterChange={setSignalFilter} />

      <div className="space-y-3">
        {visibleSignals.map((sig, index) => (
          <SignalCard
            key={sig.symbol}
            sig={sig}
            index={index}
            isAuthenticated={isAuthenticated}
            onTakeTrade={onTakeTrade}
            onNavigate={onNavigate}
          />
        ))}
      </div>

      <div className="text-center pt-4">
        <p className="text-xs text-gray-600">
          Signals generated at {new Date(signals.generated_at).toLocaleTimeString()} | {signals.stats.ai_enhanced_count} signals AI-enhanced
        </p>
      </div>
    </div>
  );
}
